import React, { useCallback, useState } from 'react'
import { OnchainKitProvider } from '@coinbase/onchainkit'
import { FundButton } from '@coinbase/onchainkit/fund'
import { Panel } from '@components/Panel/Panel'
import { useAutoGenerateCoinbaseSessionToken } from '@components/Web3/Onramp/useAutoGenerateCoinbaseSessionToken'
import { Box, Icon, IconButton, IconName, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { useEnvironment } from 'hooks/useEnvironment'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useStore } from 'store/store'
import { env } from 'utils/environment'
import { shortAddress } from 'workers/utils'

import { useTradingWalletAddresses } from './hooks/useTradingWalletAddresses'

export const TradingDepositPanel = () => {
    const { evmWalletAddress, solanaWalletAddress } = useTradingWalletAddresses()
    const { enableUsdc } = useFeatureFlags()

    const setFundWalletModalType = useStore((state) => state.setFundWalletModalType)
    const handleDepositEth = () => {
        setFundWalletModalType('eth')
    }
    const handleDepositUSDCQR = () => {
        setFundWalletModalType('usdc-crypto')
    }

    return (
        <Panel padding label="Deposit">
            {evmWalletAddress && enableUsdc && (
                <USDCWalletRow onClickQRCode={handleDepositUSDCQR} />
            )}

            {solanaWalletAddress && (
                <WalletRow walletAddress={solanaWalletAddress} name="Solana" iconName="solana" />
            )}
            {evmWalletAddress && (
                <>
                    <WalletRow
                        enableUsdc={enableUsdc}
                        walletAddress={evmWalletAddress}
                        name="Base ETH"
                        iconName="baseEth"
                        onClickFundWallet={handleDepositEth}
                    />
                </>
            )}
        </Panel>
    )
}

const WalletRow = ({
    enableUsdc,
    walletAddress,
    name,
    iconName,
    onClickFundWallet,
}: {
    walletAddress: string
    name: string
    iconName: IconName
    onClickFundWallet?: () => void
    enableUsdc?: boolean
}) => {
    const [copied, setCopied] = useState(false)
    const [, copy] = useCopyToClipboard()

    const handleCopy = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault()
            const success = await copy(walletAddress)
            setCopied(success)
            if (success) {
                setTimeout(() => setCopied(false), 1000)
            }
        },
        [setCopied, copy, walletAddress],
    )
    return (
        <Stack horizontal padding gap background="level2" rounded="md">
            <Icon type={iconName} size="square_lg" />
            <Stack gap="sm">
                <Text color="default" fontWeight="medium">
                    {name}
                </Text>
                <Text color="gray2">{shortAddress(walletAddress)}</Text>
            </Stack>
            <Box grow />
            {onClickFundWallet && !enableUsdc && (
                <IconButton
                    icon="wallet"
                    tooltip="Add funds"
                    color="default"
                    onClick={onClickFundWallet}
                />
            )}

            <IconButton
                icon="copy"
                tooltip={copied ? 'Copied!' : 'Copy address'}
                color="default"
                onClick={handleCopy}
            />
        </Stack>
    )
}

const USDCWalletRow = ({ onClickQRCode }: { onClickQRCode: () => void }) => {
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const { token: sessionToken } = useAutoGenerateCoinbaseSessionToken()
    const { baseChain } = useEnvironment()

    return (
        <Stack horizontal padding gap="sm" background="level2" rounded="md">
            <Icon type="usdcBase" size="square_lg" />
            <Stack gap="sm">
                <Text color="default" fontWeight="medium">
                    USDC
                </Text>
                {abstractAccountAddress && (
                    <Text color="gray2">{shortAddress(abstractAccountAddress)}</Text>
                )}
            </Stack>
            <Box grow />

            <OnchainKitProvider apiKey={env.VITE_COINBASE_API_KEY} chain={baseChain}>
                {sessionToken ? (
                    <FundButton
                        sessionToken={sessionToken}
                        render={(props) => (
                            <IconButton
                                icon="creditCard"
                                tooltip="Buy USDC"
                                color="default"
                                onClick={props.onClick}
                            />
                        )}
                    />
                ) : (
                    <IconButton disabled icon="creditCard" tooltip="Buy USDC" color="default" />
                )}
            </OnchainKitProvider>
            <IconButton
                icon="qrCode"
                tooltip="Receive USDC"
                color="default"
                onClick={onClickQRCode}
            />
        </Stack>
    )
}
